import { jwtDecode } from 'jwt-decode';
import { B2CIdTokenClaims } from '@/types/user';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser';
import { isAuthConfigured, loginRequest } from '@/authConfig';
import { getItemFromStore } from '.';

const AAD_B2C_SESSION_KEY = `msal.token.keys.${
  import.meta.env.VITE_MSAL_CLIENT_ID
}`;

const createSessionStorageProxy = () => {
  const originalSetItem = sessionStorage.setItem;

  sessionStorage.setItem = function (key: string, value: string) {
    originalSetItem.apply(this, [key, value]);

    const event = new StorageEvent('sessionStorageChanged', {
      key: key,
      newValue: value,
      storageArea: sessionStorage
    });
    window.dispatchEvent(event);
  };
};

export const createStorageListener = (
  callback: (token: string | null) => void,
  msalInstance: PublicClientApplication
) => {
  if (!isAuthConfigured) {
    return () => {};
  }

  createSessionStorageProxy();

  const handleStorageChange = async (event: StorageEvent) => {
    if (event.key?.includes(AAD_B2C_SESSION_KEY)) {
      const accessToken = await getUserAccessToken(msalInstance);
      callback(accessToken);
    }
  };

  window.addEventListener('sessionStorageChanged', (e: Event) => {
    handleStorageChange(e as StorageEvent);
  });

  return () => {
    window.removeEventListener('sessionStorageChanged', (e: Event) => {
      handleStorageChange(e as StorageEvent);
    });
  };
};

export const getUserAccessToken = async (
  msalInstance: PublicClientApplication
) => {
  try {
    if (!isAuthConfigured) {
      return null;
    }

    const account = msalInstance?.getActiveAccount();

    if (!account) {
      const sessionData = getItemFromStore(
        AAD_B2C_SESSION_KEY,
        null,
        sessionStorage
      );

      const accessToken = sessionData?.accessToken?.[0];

      if (!accessToken) {
        console.error('No active account or cached access token found');
        return null;
      }

      const tokenData = getItemFromStore(accessToken, null, sessionStorage);

      return tokenData?.secret || null;
    }

    const sessionData = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: account
    });

    if (!sessionData?.accessToken) {
      return null;
    }

    const accessToken = sessionData.accessToken;

    return accessToken || null;
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

export const getUserData = async (
  msalInstance: PublicClientApplication,
  retryCount = 3,
  retryDelay = 1000
): Promise<B2CIdTokenClaims | null> => {
  if (!isAuthConfigured) {
    return null;
  }

  const attemptGetToken = async (
    attemptsLeft: number
  ): Promise<B2CIdTokenClaims | null> => {
    const userAccessToken = await getUserAccessToken(msalInstance);

    if (userAccessToken) {
      return jwtDecode(userAccessToken);
    }

    if (attemptsLeft <= 0) {
      return null;
    }

    await new Promise(resolve => setTimeout(resolve, retryDelay));
    return attemptGetToken(attemptsLeft - 1);
  };

  return attemptGetToken(retryCount);
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const tokenData = jwtDecode(token);
    const expirationTime = (tokenData.exp || 0) * 1000;

    return Date.now() >= expirationTime - 5 * 60 * 1000;
  } catch {
    return true;
  }
};

export const handleAuthError = async (
  error: any,
  msalInstance: PublicClientApplication
) => {
  if (!isAuthConfigured) {
    throw error;
  }

  if (error instanceof InteractionRequiredAuthError) {
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (e) {
      console.error('Login redirect failed:', e);
      throw new Error('Authentication failed. Please try logging in again.');
    }
  }
  throw error;
};

export async function getNewAccessToken(
  account: AccountInfo,
  msalInstance: PublicClientApplication
) {
  try {
    if (!isAuthConfigured) {
      return null;
    }

    const silentRequest = {
      scopes: loginRequest.scopes,
      account: account
    };

    const response = await msalInstance.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    await handleAuthError(error, msalInstance);
    throw error;
  }
}
